import { useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { string, array, func } from 'prop-types'
import { DndContext, closestCenter, MouseSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const Container = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, 220px);
  gap: 20px;
  padding: 8px;
`

const SortableItem = ({ id, children, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  })

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }),
    [transform, transition, isDragging],
  )

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  )
}

export const CvsCardList = ({ keyValue = 'id', items = [], Card, onOrderChange, assignment }) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  )

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event

      if (!over) return

      if (active.id !== over.id) {
        const oldIndex = items.findIndex(item => item[keyValue] === active.id)
        const newIndex = items.findIndex(item => item[keyValue] === over.id)
        const newOrdered = arrayMove(items, oldIndex, newIndex)
        onOrderChange({ ...assignment, included_output_fields: newOrdered })
      }
    },
    [items, keyValue, onOrderChange, assignment],
  )

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <Container>
        <SortableContext items={items.map(item => item[keyValue])}>
          {items.map(item => (
            <SortableItem
              key={item[keyValue]}
              id={item[keyValue]}
              disabled={assignment.report_type === 'default'}
            >
              {Card({ item, index: items.indexOf(item) })}
            </SortableItem>
          ))}
        </SortableContext>
      </Container>
    </DndContext>
  )
}

CvsCardList.propTypes = {
  keyValue: string,
  items: array,
  Card: func.isRequired,
}
